import type React from "react";
import { useTranslations } from "next-intl";
import { Label } from "../../../ui/label";
import { Input } from "../../../ui/input";
import { cn } from "@/lib/utils";
import { CircleX } from "lucide-react";
interface InputWithIconProps extends React.InputHTMLAttributes<HTMLInputElement> {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  label?: string;
  onClearButton?: () => void;
  maxCharacters?: number;
  isNumber?: boolean;
  "data-testid"?: string;
}
export const InputA: React.FC<Omit<InputWithIconProps, "size">> = ({
  startAdornment,
  endAdornment,
  label,
  onClearButton,
  maxCharacters,
  isNumber = false,
  "data-testid": dataTestId,
  ...props
}) => {
  const t = useTranslations("components.inputA");
  return <div className="relative" data-sentry-component="InputA" data-sentry-source-file="InputA.tsx">
      {label && <Label className="pl-3 text-base font-semibold text-foreground">
          {label}
        </Label>}
      <div className="relative">
        {startAdornment && <div className="absolute inset-y-0 left-0 bottom-1 flex items-center pl-3 pointer-events-none text-lg [&_svg]:w-5 [&_svg]:h-5 [&_svg]:text-muted-foreground">
            {startAdornment}
          </div>}
        {onClearButton && props.value && <CircleX onClick={onClearButton} className="cursor-pointer absolute right-2 top-2.5 text-muted-foreground" />}
        {endAdornment && !onClearButton && <div className="absolute inset-y-0 right-0 flex items-center pr-3 z-10">
            {endAdornment}
          </div>}
        <Input {...props} data-testid={dataTestId} className={cn("border-0 border-b border-border-medium h-11 md:text-lg text-primary font-medium rounded-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none bg-transparent pl-1 placeholder:text-sm sm:placeholder:text-lg", startAdornment ? "pl-11" : "", endAdornment ? "pr-10" : "", props.className)} onChange={e => {
        let value = e.target.value;
        if (isNumber) {
          // Allow only digits and a single decimal point
          value = value.replace(/[^\d.]/g, "");
          const parts = value.split(".");
          if (parts.length > 2) {
            value = parts[0] + "." + parts.slice(1).join("");
          }
          const syntheticEvent = {
            ...e,
            target: {
              ...e.target,
              value
            }
          };
          if (maxCharacters && value.length > maxCharacters) return;
          props.onChange?.(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
          return;
        }
        if (maxCharacters && value.length > maxCharacters) return;
        props.onChange?.(e);
      }} data-sentry-element="Input" data-sentry-source-file="InputA.tsx" />
        {maxCharacters && <span className="block pl-1 text-xs sm:text-sm mt-2 text-muted-foreground">
            {t("charactersCount", {
          count: typeof props.value === "string" || Array.isArray(props.value) ? props.value.length : 0,
          max: maxCharacters
        })}
          </span>}
      </div>
    </div>;
};